/**
 * Central Amplitude client for the GG web app.
 * All logEvent calls should go through logEvent() so property keys and values
 * stay consistent (legacy + canonical keys during transition).
 *
 * Browser SDK 2 so the app shares the AMP_{first-10-of-key} cookie Webflow
 * already writes on .greatestgiftapp.com.
 */
import * as amplitude from '@amplitude/analytics-browser';
import config from './config';

let initialized = false;

/** Legacy display keys → canonical snake_case keys (both are sent during transition). */
const LEGACY_TO_CANONICAL = {
  'gift amount': 'gift_amount',
  'first fund': 'first_fund',
  'gift status': 'gift_status',
  'gift type': 'gift_type',
  'method type': 'method_type',
  'chosen amount': 'chosen_amount',
  'last page': 'last_page',
  'signup-popup': 'signup_popup',
  'user-gifting': 'user_gifting',
};

const CANONICAL_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_CANONICAL).map(([legacy, canonical]) => [canonical, legacy])
);

function legacyValueForCanonical(canonicalKey, value) {
  if (canonicalKey === 'gift_status' && value != null) {
    return String(value).toLowerCase();
  }
  if (canonicalKey === 'user_gifting') {
    return value ? 'true' : 'false';
  }
  if (canonicalKey === 'signup_popup') {
    return value ? 'Open' : 'Close';
  }
  return value;
}

/** Normalize and mirror legacy ↔ canonical property keys on every event payload. */
export function expandEventProperties(properties = {}) {
  const out = { ...properties };

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) continue;

    const canonical = LEGACY_TO_CANONICAL[key];
    if (canonical) {
      out[canonical] = legacyValueForCanonical(canonical, value);
    }

    const legacy = CANONICAL_TO_LEGACY[key];
    if (legacy) {
      out[legacy] = legacyValueForCanonical(key, value);
    }
  }

  if (out.gift_status != null) {
    out.gift_status = String(out.gift_status).toLowerCase();
    out['gift status'] = out.gift_status;
  } else if (out['gift status'] != null) {
    out['gift status'] = String(out['gift status']).toLowerCase();
    out.gift_status = out['gift status'];
  }

  return out;
}

/** Properties for `gifting: start gift` — booleans + legacy string mirrors. */
export function giftingStartGiftProperties({ source, signupPopupOpen, userGifting }) {
  return expandEventProperties({
    source,
    signup_popup: !!signupPopupOpen,
    'signup-popup': signupPopupOpen ? 'Open' : 'Close',
    user_gifting: !!userGifting,
    'user-gifting': userGifting ? 'true' : 'false',
  });
}

function cookieOptions() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const onGgDomain = host === 'greatestgiftapp.com' || host.endsWith('.greatestgiftapp.com');
  return {
    sameSite: 'Lax',
    secure: host !== 'localhost' && host !== '127.0.0.1',
    ...(onGgDomain ? { domain: '.greatestgiftapp.com' } : {}),
  };
}

export function initAnalytics() {
  if (initialized) return;

  // autocapture off: keep the existing custom event taxonomy.
  amplitude.init(config.amp_key, {
    cookieOptions: cookieOptions(),
    autocapture: false,
    minIdLength: 1,
  });
  initialized = true;
}

export function logEvent(eventName, properties = {}) {
  initAnalytics();
  amplitude.track(eventName, expandEventProperties(properties));
}

export function setAnalyticsUserId(userId) {
  initAnalytics();
  if (userId == null || userId === '') return;
  amplitude.setUserId(String(userId));
}

/** Set durable user properties (experiment buckets, etc.) for cohorting in Amplitude. */
export function setAnalyticsUserProperties(properties = {}) {
  initAnalytics();
  if (!properties || !Object.keys(properties).length) return;

  const identify = new amplitude.Identify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined) identify.set(key, value);
  });
  amplitude.identify(identify);
}

export function clearAnalyticsUserId() {
  initAnalytics();
  // Clear user_id only
  amplitude.setUserId(undefined);
}
